import { useEffect, useRef, useState } from 'react';
import { useOutletContext } from 'react-router';
import { formatDate, resizeImgFile } from '../../../lib/utils';
import DailyMealPlan from './dailyMealPlan';
import RecentMeal from './recentMeal';
import NewMeal from './newMeal';
import ViewPhoto from './viewPhoto';
import FeedPhoto from './feedPhoto';
import CapturePhoto from '../../../components/capturePhoto';
import request from '../../../lib/request';
import PayDialog from '../../../components/premium/payDialog';
import MealStats from './mealStats';

const MealTracker = () => {
  const { user, refreshUser } = useOutletContext();

  const saveDialogRef = useRef(null);
  const viewDialogRef = useRef(null);
  const feedDialogRef = useRef(null);
  const fileRef = useRef(null);
  const payDialogRef = useRef(null);

  const [source, setSource] = useState(null);
  const [currentFile, setCurrentFile] = useState(null);
  const [prevMeals, setPrevMeals] = useState([]);
  const [lastMealInfo, setLastMealInfo] = useState(null);
  const [todayMeals, setTodayMeals] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isFeedDialogOpen, setIsFeedDialogOpen] = useState(false);
  const [isIngredientDialogOpen, setIsIngredientDialogOpen] = useState(false);

  useEffect(() => {
    if (!isFeedDialogOpen) {
      getRecentMeal();
      getTodayTotalCalories();
    }
  }, [isFeedDialogOpen]);

  const openSaveDialog = () => {
    if (saveDialogRef.current) {
      saveDialogRef.current.showModal();
      setDialogOpen(true);
    }
  };

  const closeSaveDialog = () => {
    if (saveDialogRef.current) {
      saveDialogRef.current.close();
      setDialogOpen(false);
    }
  };

  const openViewDialog = () => {
    if (viewDialogRef.current) {
      viewDialogRef.current.showModal();
      setDialogOpen(true);
    }
  };

  const closeViewDialog = () => {
    if (viewDialogRef.current) {
      viewDialogRef.current.close();
      setDialogOpen(false);
    }
  };

  const openFeedDialog = () => {
    if (feedDialogRef.current) {
      feedDialogRef.current.showModal();
      setDialogOpen(true);
      setIsFeedDialogOpen(true);
    }
  };

  const closeFeedDialog = () => {
    if (feedDialogRef.current) {
      feedDialogRef.current.close();
      setDialogOpen(false);
      setIsFeedDialogOpen(false);
    }
  };

  const handleClickCapture = () => {
    if (fileRef.current && fileRef.current.value) {
      fileRef.current.value = '';
    }
  };

  const handleCapture = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      let resizedFile = await resizeImgFile(file);

      if (file.size < resizedFile.size) {
        resizedFile = file;
      }

      const newUrl = URL.createObjectURL(resizedFile);

      setCurrentFile(resizedFile);
      setSource(newUrl);
      openSaveDialog();
    }
  };

  const getRecentMeal = async () => {
    try {
      const count = 4;
      const res = await request.get(`/meal/recent?count=${count}`);
      if (Array.isArray(res.result)) {
        const prevMealsRes = res.result.slice(1);
        prevMealsRes.reverse();
        const lastMealInfoRes = res.result[0];

        if (prevMealsRes.length < 3) {
          for (let i = prevMealsRes.length; i < 3; i++) {
            prevMealsRes.unshift(null);
          }
        }

        setPrevMeals(prevMealsRes);
        setLastMealInfo(lastMealInfoRes);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const getTodayTotalCalories = async () => {
    try {
      const today = formatDate(new Date());
      const startDate = new Date(`${today}T00:00:00`).toISOString();
      const endDate = new Date(`${today}T23:59:59.999`).toISOString();
      const { result } = await request.get(
        `/meal/today?date=${today}&startDate=${startDate}&endDate=${endDate}`
      );

      if (result) {
        setTodayMeals(result);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const openPayDialog = () => {
    if (payDialogRef.current) {
      payDialogRef.current.showModal();
    }
  };

  const closePayDialog = () => {
    if (payDialogRef.current) {
      payDialogRef.current.close();
    }
  };

  const handleRefresh = async () => {
    await getRecentMeal();
    await getTodayTotalCalories();
  };

  return (
    <>
      <div
        className={`space-y-4 p-4 ${
          dialogOpen ? 'h-10 overflow-hidden' : 'pb-32'
        }`}
      >
        <h1 className='text-2xl font-bold text-white'>Meal Tracker</h1>
        {user.programRegistered && <DailyMealPlan />}
        <MealStats todayMeals={todayMeals} />
        <RecentMeal
          openFeedDialog={openFeedDialog}
          openViewDialog={openViewDialog}
          prevMeals={prevMeals}
          lastMealInfo={lastMealInfo}
          isIngredientDialogOpen={isIngredientDialogOpen}
          setIsIngredientDialogOpen={setIsIngredientDialogOpen}
        />
        <div className='flex justify-center text-white'>
          <CapturePhoto
            fileRef={fileRef}
            handleCapture={handleCapture}
            handleClickCapture={handleClickCapture}
          />
        </div>
      </div>
      <NewMeal
        saveDialogRef={saveDialogRef}
        closeDialog={closeSaveDialog}
        source={source}
        setSource={setSource}
        currentFile={currentFile}
        setCurrentFile={setCurrentFile}
        handleRefresh={handleRefresh}
        isIngredientDialogOpen={isIngredientDialogOpen}
        setIsIngredientDialogOpen={setIsIngredientDialogOpen}
        user={user}
        openPayDialog={openPayDialog}
      />
      <ViewPhoto
        viewDialogRef={viewDialogRef}
        closeDialog={closeViewDialog}
        mealInfo={lastMealInfo}
        handleRefresh={handleRefresh}
        isIngredientDialogOpen={isIngredientDialogOpen}
        setIsIngredientDialogOpen={setIsIngredientDialogOpen}
        user={user}
        openPayDialog={openPayDialog}
      />
      <FeedPhoto
        isDialogOpen={isFeedDialogOpen}
        feedDialogRef={feedDialogRef}
        closeDialog={closeFeedDialog}
        isIngredientDialogOpen={isIngredientDialogOpen}
        setIsIngredientDialogOpen={setIsIngredientDialogOpen}
      />
      <PayDialog
        dialogRef={payDialogRef}
        closeDialog={closePayDialog}
        title='Go Premium'
        refreshUser={refreshUser}
      />
    </>
  );
};

export default MealTracker;
